<script setup>
import { computed, h, ref } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import AgentCell from './AgentCell.vue';
import AnimatedMetricValue from './AnimatedMetricValue.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useToggle } from '@vueuse/core';
import { useReportLoading } from '../../composables/useReportLoading';

const {
  agents,
  agentMetrics,
  isLoading,
  pageIndex,
  totalCount: agentMetricsTotal,
} = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 0,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['filteredAgent', 'pageChange']);
const { t } = useI18n();

const selectedAgent = ref(null);
const visibleLoading = useReportLoading(() => isLoading);

function getAgentInformation(id) {
  return agents?.find(agent => agent.id === Number(id));
}
const [showAgentDropdown, toggleAngetDropdown] = useToggle();

const rowsForMetrics = metrics =>
  metrics
    .filter(agentMetric => getAgentInformation(agentMetric.id))
    .filter(agentMetric => {
      const {
        open,
        unattended,
        resolved_today: resolvedToday,
      } = agentMetric.metric || {};
      return [open, unattended, resolvedToday].some(value => Number(value) > 0);
    })
    .map(agent => {
      const agentInformation = getAgentInformation(agent.id);
      if (!agentInformation) {
        return null;
      }

      const teams = agent.teams.length
        ? agent.teams.map(team => team.name).join(', ')
        : 0;

      return {
        agent: agentInformation.name || agentInformation.available_name,
        email: agentInformation.email,
        thumbnail: agentInformation.thumbnail,
        open: agent.metric.open || 0,
        teams: teams,
        unattended: agent.metric.unattended || 0,
        resolved_today: agent.metric.resolved_today || 0,
        status: agentInformation.availability_status,
      };
    })
    .filter(Boolean);

const tableData = computed(() => rowsForMetrics(agentMetrics));

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const createMetricSpanRender = activeClass => cellProps =>
  h(AnimatedMetricValue, {
    value: cellProps.getValue(),
    valueClass: activeClass,
  });

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps => h(AgentCell, cellProps),

    size: 250,
  }),
  columnHelper.accessor('teams', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.TEAM'),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: createMetricSpanRender('text-woot-800 dark:text-woot-300'),
    size: 100,
  }),
  columnHelper.accessor('unattended', {
    header: () =>
      h(
        'span',
        {
          title: 'Clientes que estão aguardando resposta do agente',
        },
        t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED')
      ),
    cell: createMetricSpanRender('text-orange-600 dark:text-orange-400'),
    size: 100,
  }),
  columnHelper.accessor('resolved_today', {
    header: 'Resolvidas hoje',
    cell: createMetricSpanRender('text-green-600 dark:text-green-400'),
    size: 120,
  }),
];

const agentMenuItems = computed(() => {
  return [
    {
      label: 'Todos os agentes',
      value: null,
      action: 'select_agent',
    },
    ...agents.map(agent => ({
      label: agent.name,
      value: agent.id,
      action: 'select_agent',
    })),
  ];
});

const selectedAgentFilter = computed(() => {
  if (!selectedAgent.value) {
    return { label: 'Todos os agentes' };
  }
  return agentMenuItems.value.find(
    item => item.value === selectedAgent.value.id
  );
});

const handleAgentAction = ({ value }) => {
  toggleAngetDropdown(false);
  selectedAgent.value = value ? agents.find(agent => agent.id === value) : null;
  emit('filteredAgent', selectedAgent?.value?.id);
};

const totalCount = computed(() => agentMetricsTotal);
const paginationState = computed(() => ({
  pageIndex,
  pageSize: 25,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalCount.value;
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
  },
  onPaginationChange: updater => {
    const pagination = updater(paginationState.value);
    emit('pageChange', pagination.pageIndex);
  },
});
</script>

<template>
  <div class="agent-table-container">
    <div class="flex items-center justify-end gap-3 mb-4">
      <div
        v-on-clickaway="() => toggleAngetDropdown(false)"
        class="relative group flex flex-row items-center"
      >
        <Button
          sm
          slate
          :label="selectedAgentFilter.label"
          class="rounded-md group-hover:bg-n-alpha-2 max-w-full"
          @click="toggleAngetDropdown()"
        />
        <DropdownMenu
          v-if="showAgentDropdown"
          :menu-items="agentMenuItems"
          show-search
          search-placeholder="Buscar agente"
          class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full !min-w-56 max-w-full max-h-96 overflow-y-auto"
          @action="handleAgentAction($event)"
        />
      </div>
    </div>
    <Table
      :table="table"
      class="max-h-[calc(100vh-21.875rem)] border border-slate-50 dark:border-slate-800"
    />
    <Pagination v-if="totalCount > 25" class="mt-2" :table="table" />
    <div v-if="visibleLoading" class="agents-loader">
      <Spinner />
      <span>{{
        $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
      }}</span>
    </div>
    <EmptyState
      v-else-if="!visibleLoading && !tableData.length"
      :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
    />
  </div>
</template>

<style lang="scss" scoped>
.agent-table-container {
  @apply flex flex-col flex-1;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  .row-user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col min-w-0 my-0 mx-2;

      .title {
        @apply text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100;
      }

      .sub-title {
        @apply text-xs text-slate-600 dark:text-slate-200;
      }
    }
  }

  .table-pagination {
    @apply mt-4 text-right;
  }
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
